import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isNil } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import {
  ModusAutocomplete,
  ModusAutocompleteModule,
  ModusIconModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { debounceTime, filter, finalize, map } from 'rxjs';
import { ErrorState } from '../../../../error-handling/error.state';
import { modusOptionListTranslations } from '../../../../i18n/modus-option-list.translations';
import { ScandataModel, UpdateScandataModel } from '../../../../scandata/scandata.models';
import { ScandataService } from '../../../../scandata/scandata.service';
import { MAX_TAG_LENGTH, TagService } from '../../../../tag/tag.service';
import { NotWhitespaceStringValidator } from '../../../../utils/not-whitespace-string-validator';
import { taggingTranslations } from './tagging.translations';

@UntilDestroy()
@Component({
  selector: 'sd-tagging',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusAutocompleteModule,
    ModusIconModule,
    ModusTooltipModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './tagging.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaggingComponent implements AfterViewInit {
  private readonly store = inject(Store);
  private readonly tagService = inject(TagService);
  private readonly scandataService = inject(ScandataService);
  private readonly translationService = inject(TranslationService);

  readonly translations = { ...taggingTranslations(), ...modusOptionListTranslations() };

  readonly scandataModel = input.required<ScandataModel>();
  readonly readonly = input(false);

  readonly saving = output<boolean>();

  private readonly busySaving = linkedSignal({
    source: this.scandataModel,
    computation: () => false,
  });

  readonly saveError = this.store.selectSignal(ErrorState.hasError('scanDetailsSaveError'));

  readonly disableEdit = computed(() => this.readonly() || this.busySaving());

  readonly tagSelector = this.getTagSelector();
  private readonly tagSelectorValue = this.getTagSelectorValue();
  readonly tagSelectorError = this.getTagSelectorError();

  private readonly projectTags = this.getProjectTags();
  readonly selectedTags = linkedSignal(() => this.scandataModel().tags ?? []);
  readonly filteredTags = this.getFilteredTags();

  private readonly autocomplete = viewChild.required<ModusAutocomplete<string>>('autocomplete');

  constructor() {
    this.createSavingEffect();
    this.createDisableEffect();
  }

  ngAfterViewInit(): void {
    this.observeAutoCompleteKeyDown();
  }

  selected(tag: string): void {
    if (this.tagSelector.invalid || isNil(tag)) return;

    this.selectedTags.update((tags) => [...tags, tag]);
    this.tagSelector.reset();
    this.saveScandataModel();
  }

  remove(tag: string): void {
    const index = this.selectedTags().indexOf(tag);

    if (index >= 0) {
      this.selectedTags.update((tags) => tags.toSpliced(index, 1));
      this.tagSelector.setValue(null);
      this.saveScandataModel();
    }
  }

  private observeAutoCompleteKeyDown() {
    this.autocomplete()
      .inputKeydown$.pipe(
        filter((event) => event.key === 'Enter'),
        filter(() => isNil(this.autocomplete().activeOption)),
        filter(() => this.tagSelector.dirty && this.tagSelector.valid),
        filter(() => this.tagNotSelected()),
        map(() => this.findOrAddTag()),
        untilDestroyed(this),
      )
      .subscribe((tag) => {
        this.selected(tag);
        this.autocomplete().closePanel();
      });
  }

  private createSavingEffect() {
    effect(() => {
      this.saving.emit(this.busySaving());
    });
  }

  private createDisableEffect() {
    effect(() => {
      if (this.disableEdit()) {
        this.tagSelector.disable({ emitEvent: false });
      } else {
        this.tagSelector.enable({ emitEvent: false });
      }
    });
  }

  private tagNotSelected() {
    const value = this.tagSelector.value as string;
    const index = this.selectedTags().findIndex((tag) => tag.toLowerCase() === value.toLowerCase());
    return index === -1;
  }

  private findOrAddTag() {
    const value = this.tagSelector.value as string;
    const tag = this.projectTags().find((tag) => tag.toLowerCase() === value.toLowerCase());
    return tag ?? value;
  }

  private setSelectedTags(scan: ScandataModel) {
    const scanTags = scan.tags ?? [];
    this.selectedTags.set([...scanTags]);
  }

  private getTagSelector() {
    return new FormControl<string | null>(null, {
      validators: [NotWhitespaceStringValidator, Validators.maxLength(MAX_TAG_LENGTH)],
      updateOn: 'change',
    });
  }

  private getTagSelectorValue() {
    return toSignal(this.tagSelector.valueChanges.pipe(debounceTime(100)), {
      initialValue: null,
    });
  }

  private getTagSelectorError() {
    return toSignal(
      this.tagSelector.statusChanges.pipe(
        map(() => {
          if (!this.tagSelector.hasError('maxlength')) return '';

          const text = this.translations.tagInput.lengthExceededError.text;
          return this.translationService.translate(text, { maxCharacters: MAX_TAG_LENGTH });
        }),
      ),
      { initialValue: '' },
    );
  }

  private saveScandataModel() {
    this.busySaving.set(true);

    const updateScandataModel: UpdateScandataModel = {
      id: this.scandataModel().id,
      tags: this.selectedTags(),
    };

    return this.scandataService
      .updateScandataModel(updateScandataModel)
      .pipe(finalize(() => this.busySaving.set(false)))
      .subscribe({
        error: () => {
          this.setSelectedTags(this.scandataModel());
        },
      });
  }

  private getProjectTags() {
    return toSignal(this.tagService.getTags(), { initialValue: [] });
  }

  private getFilteredTags() {
    return computed(() => {
      const projectTags = this.projectTags();
      const selectedTags = this.selectedTags();
      const filter = this.tagSelectorValue();

      const availableTags = projectTags.filter((tag) => !selectedTags.includes(tag));
      if (isNil(filter)) return availableTags;

      const filterValue = filter.toLowerCase();
      return availableTags.filter((tag) => tag.toLowerCase().includes(filterValue));
    });
  }
}
